import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from "react";

export type LangCode = "iw" | "ru" | "en";

const VALID_LANGS: LangCode[] = ["iw", "ru", "en"];
const LANG_ALIASES: Record<string, LangCode> = { he: "iw", hebrew: "iw", ru: "ru", russian: "ru", en: "en", english: "en" };
const STORAGE_KEY = "smileyland_lang";

function resolveLangParam(raw: string | null): LangCode | null {
  if (!raw) return null;
  const lower = raw.toLowerCase();
  if (VALID_LANGS.includes(lower as LangCode)) return lower as LangCode;
  return LANG_ALIASES[lower] || null;
}

function getInitialLang(): LangCode {
  if (typeof window === "undefined") return "iw";
  // 1. URL param takes priority
  const params = new URLSearchParams(window.location.search);
  const fromUrl = resolveLangParam(params.get("lang"));
  if (fromUrl) return fromUrl;
  // 2. localStorage
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && VALID_LANGS.includes(stored as LangCode)) return stored as LangCode;
  return "iw";
}

/** Trigger Google Translate programmatically */
function triggerGoogleTranslate(langCode: LangCode) {
  if (langCode === "iw") return;
  const tryTrigger = () => {
    const select = document.querySelector<HTMLSelectElement>(".goog-te-combo");
    if (select) {
      select.value = langCode;
      select.dispatchEvent(new Event("change"));
      return true;
    }
    return false;
  };
  // Google Translate widget may not be ready yet — retry a few times
  if (!tryTrigger()) {
    let attempts = 0;
    const interval = setInterval(() => {
      if (tryTrigger() || ++attempts > 20) clearInterval(interval);
    }, 300);
  }
}

interface LanguageContextValue {
  lang: LangCode;
  setLang: (code: LangCode) => void;
}

const LanguageContext = createContext<LanguageContextValue>({
  lang: "iw",
  setLang: () => {},
});

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [lang, setLangState] = useState<LangCode>(getInitialLang);

  const setLang = useCallback((code: LangCode) => {
    setLangState(code);
    localStorage.setItem(STORAGE_KEY, code);
    // Update URL param without reload
    const url = new URL(window.location.href);
    if (code === "iw") {
      url.searchParams.delete("lang");
    } else {
      url.searchParams.set("lang", code);
    }
    window.history.replaceState({}, "", url.toString());
  }, []);

  // On mount, if lang !== "iw", trigger Google Translate
  useEffect(() => {
    if (lang !== "iw") {
      localStorage.setItem(STORAGE_KEY, lang);
      triggerGoogleTranslate(lang);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <LanguageContext.Provider value={{ lang, setLang }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
